<script lang="ts">
    import { DEFAULT_LAYOUT } from '$lib/components/constants';

    import { VideoAspectRatio } from '$lib/types/Components/Video/VideoContent';
    import { cn } from '$lib/utils/cn';
    import { isMobileDevice } from '$lib/utils/isMobileDevice';

    import { onMount, onDestroy } from 'svelte';
    import Player from 'svelte-react-player';

    import Controls from './Controls.svelte';
    import { preloadVideoPlayerAssets } from '../_utils/video';
    import Box from '../_wrappers/Box.svelte';

    import type { Box as BoxProps } from '$lib/types/Components/SharedTypes';
    import type { Language } from '$lib/types/Language';
    import type { TransitionEffect } from '$lib/types/Theme';
    import type { SeekTo } from './Video.types';

    export let id = '';
    export let url = '';
    export let thumbnail: string;
    export let autoplay = false;
    export let transitionDelay = 0;
    export let transitionEffect: TransitionEffect = 'default';
    export let box: BoxProps;
    export let borderRadiusClass = '';
    export let aspectRatio: VideoAspectRatio = VideoAspectRatio.Horizontal;
    export let layout = DEFAULT_LAYOUT;
    export let customControls = false;
    export let isCustomVideo = false;
    export let showSubtitles = false;
    export let language: Language = 'en';

    const DEFAULT_VOLUME = 50;
    const TRANSLATIONS = {
        en: {
            play: 'Play video',
            pause: 'Pause video',
        },
        de: {
            play: 'Video abspielen',
            pause: 'Video pausieren',
        },
    };
    const VIDEO_EL_ID = `video-${id}`;

    let playing = false;
    let isFullscreen = false;
    let duration = 0;
    let playedDuration = 0;
    let volume = DEFAULT_VOLUME;
    let hasVisibleCaptions = false;
    let hasSubtitleTracks = false;
    let videoWrapper: HTMLDivElement;

    let seekTo: SeekTo = () => {};

    const { isInFramedColumn, isInColumn } = layout;

    if (autoplay) {
        playing = true;
        volume = 0;
    }

    const handleReady = (params: { wrapper: HTMLDivElement; seekTo: SeekTo }) => {
        const videoEl = document.getElementById(VIDEO_EL_ID) as HTMLVideoElement | null;

        // Enable native controls on mobile where custom controls are disabled
        if (videoEl && !customControls) {
            videoEl.controls = true;
        }

        // Initially hide captions if autoplay is enabled and video is muted
        const track = videoEl?.textTracks?.[0];

        if (track) {
            hasSubtitleTracks = true;
            track.mode = 'disabled';
        }

        // Some HLS streams buffer starting at a non-zero timestamp (e.g. first segment
        // at t=5.95s). When play() is called at currentTime=0, the browser fires
        // 'waiting' indefinitely because there is no data at that position. Detect
        // this gap on 'waiting' (user already clicked play, buffer already filled) and
        // on 'progress' (autoplay: play() queued before first segment arrives), then
        // advance currentTime to the first buffered position so playback can begin.
        // Linear Ticket: https://linear.app/perspective/issue/FUN-3459/desktop-hls-video-sometimes-wont-start-playing
        if (videoEl) {
            const cleanupBufferGapListeners = () => {
                videoEl.removeEventListener('waiting', fixBufferGap);
                videoEl.removeEventListener('progress', fixBufferGap);
                videoEl.removeEventListener('playing', cleanupBufferGapListeners);
            };
            const fixBufferGap = () => {
                if (
                    !videoEl.paused &&
                    videoEl.buffered.length > 0 &&
                    videoEl.buffered.start(0) > videoEl.currentTime
                ) {
                    cleanupBufferGapListeners();
                    videoEl.currentTime = videoEl.buffered.start(0);
                }
            };
            videoEl.addEventListener('waiting', fixBufferGap);
            videoEl.addEventListener('progress', fixBufferGap);
            videoEl.addEventListener('playing', cleanupBufferGapListeners);
        }

        // If the video is not in a framed column, we need to add the border radius class to the iframe
        const { wrapper } = params;

        seekTo = params.seekTo;

        if (wrapper) {
            const iframeElements = wrapper.getElementsByTagName('iframe');

            if (iframeElements.length !== 1) {
                return;
            }

            const iFrameClasses = cn(borderRadiusClass, {
                'max-sm:fun-rounded-none': !layout.isInFramedColumn,
            });

            iframeElements[0].classList.add(
                ...iFrameClasses.split(' ').filter((iframeClass) => iframeClass.length > 0),
            );
        }
    };

    $: videoWrapperClass = cn('fun-group/video fun-overflow-hidden', borderRadiusClass, {
        'fun-w-[calc(100%+32px)] fun--translate-x-4 sm:fun-w-full sm:fun-translate-x-0':
            !isInFramedColumn && isInColumn,
        'fun-flex fun-h-screen fun-w-screen fun-flex-col fun-justify-center':
            isFullscreen && customControls,
        'fun-relative': customControls,
        'fun-mx-auto fun-max-w-[416px]': aspectRatio === VideoAspectRatio.Vertical,
        'fun-mx-auto fun-max-w-[480px] [&_video]:fun-object-cover':
            aspectRatio === VideoAspectRatio.Square || aspectRatio === VideoAspectRatio.Vertical,
    });

    const togglePlaying = () => {
        playing = !playing;
    };

    const toggleMuted = () => {
        volume = volume === 0 ? DEFAULT_VOLUME : 0;
    };

    const toggleCaptions = () => {
        const videoEl = document.getElementById(VIDEO_EL_ID) as HTMLVideoElement | null;

        const track = videoEl?.textTracks?.[0];

        if (track) {
            if (track.mode === 'showing') {
                track.mode = 'disabled';
                hasVisibleCaptions = false;
            } else {
                track.mode = 'showing';
                hasVisibleCaptions = true;
            }
        }
    };

    const onProgress = (progress: { playedSeconds: number; loadedSeconds: number }) => {
        playedDuration = progress.playedSeconds;
    };

    const onDuration = (_duration: number) => {
        duration = _duration;
    };

    const changeVolume = (_volume: number) => {
        volume = Math.max(0, Math.min(_volume, 100));
    };

    const toggleFullscreen = async () => {
        if (!videoWrapper) return;

        try {
            if (!isFullscreen) {
                await videoWrapper.requestFullscreen();
                isFullscreen = true;
            } else {
                await document.exitFullscreen();
                isFullscreen = false;
            }
        } catch (error) {
            console.warn('Fullscreen API not supported or failed:', error);
        }
    };

    const handleFullscreenChange = () => {
        // This is a workaround to handle the case where the user exits fullscreen by pressing the ESC key.
        // This is not meant to replace the manual fullscreen toggle and is only used as a fallback to ensure the fullscreen state is set to false when the user exits fullscreen.
        if (!document.fullscreenElement) {
            isFullscreen = false;
        }
    };

    onMount(() => {
        document.addEventListener('fullscreenchange', handleFullscreenChange);
    });

    onDestroy(() => {
        document.removeEventListener('fullscreenchange', handleFullscreenChange);
    });

    // Add the correct aspect ratio parameter to the imgix thumbnail URL
    const getThumbnailWithAspectRatio = () => {
        if (!thumbnail) return thumbnail;

        const aspectRatioMap = {
            [VideoAspectRatio.Vertical]: '9:16',
            [VideoAspectRatio.Square]: '1:1',
            [VideoAspectRatio.Horizontal]: '16:9',
        };

        const targetAspectRatio = aspectRatioMap[aspectRatio] || '16:9';

        try {
            const url = new URL(thumbnail);
            url.searchParams.set('ar', targetAspectRatio);
            return url.toString();
        } catch {
            // If URL parsing fails, return original thumbnail
            return thumbnail;
        }
    };

    const thumbnailWithAspectRatio = getThumbnailWithAspectRatio();

    // Map aspect ratios to Player ratio percentages
    const playerRatioMap = {
        [VideoAspectRatio.Vertical]: '177.77%', // 9:16
        [VideoAspectRatio.Square]: '100%', // 1:1
        [VideoAspectRatio.Horizontal]: '56.25%', // 16:9
    };

    const playerRatio = playerRatioMap[aspectRatio] || '56.25%';

    // FUN-973: The wistia video player loads a lot of assets one after the other, causing a delay
    // of at least 1-1,5 seconds. The delay may be even greater depending on the network speed.
    // We cannot preload Wistia assets in the mapper component because we first need consensus
    // for that (cookie overlay). But we can preload/kick off loading all assets in parallel.
    // That's exactly what we are doing here.
    $: {
        if (url) {
            preloadVideoPlayerAssets(url);
        }
    }
</script>

<Box {id} {...box} {transitionDelay} {transitionEffect}>
    <div bind:this={videoWrapper} class={videoWrapperClass}>
        {#if customControls}
            <div
                class={cn(
                    'fun-absolute fun-inset-0 fun-z-10 fun-flex fun-items-center fun-justify-center focus-visible:fun--outline-offset-1',
                    borderRadiusClass,
                )}
                tabindex="0"
                role="button"
                on:click={() => {
                    if (customControls) togglePlaying();
                }}
                on:keydown={(event) => {
                    if (customControls && event.code === 'Space') togglePlaying();
                }}
                aria-label={playing ? TRANSLATIONS[language].pause : TRANSLATIONS[language].play}
            >
                {#if !playing}
                    <div
                        class="fun-flex fun-h-16 fun-w-16 fun-items-center fun-justify-center fun-rounded-full fun-bg-white fun-drop-shadow"
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 20 20"
                            fill="none"
                        >
                            <path
                                d="M6.29995 2.84095C5.3011 2.21124 4 2.92906 4 4.10984V15.891C4 17.0718 5.3011 17.7896 6.29995 17.1599L15.6436 11.2693C16.577 10.6809 16.577 9.31997 15.6436 8.73152L6.29995 2.84095Z"
                                fill="#111827"
                            />
                        </svg>
                    </div>
                {/if}
            </div>
        {:else if isCustomVideo && !playing}
            <div
                class={cn(
                    'fun-absolute fun-inset-0 fun-z-10 fun-flex fun-items-center fun-justify-center',
                    borderRadiusClass,
                )}
                tabindex="0"
                role="button"
                on:click={togglePlaying}
                on:keydown={(event) => {
                    if (event.code === 'Space') togglePlaying();
                }}
                aria-label={TRANSLATIONS[language].play}
            >
                <div
                    class="fun-flex fun-h-16 fun-w-16 fun-items-center fun-justify-center fun-rounded-full fun-bg-white fun-drop-shadow"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 20 20"
                        fill="none"
                    >
                        <path
                            d="M6.29995 2.84095C5.3011 2.21124 4 2.92906 4 4.10984V15.891C4 17.0718 5.3011 17.7896 6.29995 17.1599L15.6436 11.2693C16.577 10.6809 16.577 9.31997 15.6436 8.73152L6.29995 2.84095Z"
                            fill="#111827"
                        />
                    </svg>
                </div>
            </div>
        {/if}

        <Player
            {url}
            ratio={playerRatio}
            fluid
            muted={volume === 0}
            {playing}
            volume={customControls ? volume / 100 : undefined}
            controls={!customControls}
            config={{
                playerOptions: { pip: 1, playsInline: 1 },
                attributes: { poster: isCustomVideo ? thumbnailWithAspectRatio : undefined },
                vimeo: { playerOptions: { playsinline: 1 } }, // Required besides top-level playsInline
                file: {
                    forceHLS: !isMobileDevice && url.includes('hls'),
                    hlsVersion: '1.5.17',
                    attributes: {
                        crossOrigin: 'anonymous',
                        playsInline: true,
                        controls: !customControls,
                        id: VIDEO_EL_ID,
                    },
                },
            }}
            callbacks={{
                onReady: handleReady,
                onProgress,
                onDuration,
                onPlay: () => {
                    playing = true;
                },
                onPause: () => {
                    playing = false;
                },
            }}
        />

        {#if customControls}
            <Controls
                {volume}
                {duration}
                {playedDuration}
                {playing}
                {hasVisibleCaptions}
                isCCButtonVisible={hasSubtitleTracks && showSubtitles}
                {togglePlaying}
                muted={volume === 0}
                {toggleMuted}
                {toggleCaptions}
                {seekTo}
                {changeVolume}
                {toggleFullscreen}
            />
        {/if}
    </div>
</Box>
